/**
 * Billing Store
 * Zustand store for managing billing configuration state across all 3 tabs
 */

import { create } from 'zustand';
import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { servicesContainer } from '@/core/di/services-container';
import { BillingApi } from '../data/billing-api';
import type {
  OperatorBillingConfig,
  UpsertOperatorBillingConfigRequest,
  ExcludedMachine,
  AddExcludedMachineRequest,
  ReimbursementFee,
  ReimbursementFeesFilters,
} from '../types/billing-types';

interface BillingStore {
  // Operator Config state
  operatorConfigs: OperatorBillingConfig[];
  isLoadingOperatorConfigs: boolean;
  operatorConfig: OperatorBillingConfig | null;
  operatorConfigExists: boolean; // true = PUT, false = POST
  isLoadingOperatorConfig: boolean;
  isSavingOperatorConfig: boolean;
  operatorConfigError: string | null;

  // Excluded Machines state
  excludedMachines: ExcludedMachine[];
  isLoadingExcludedMachines: boolean;
  excludedMachinesError: string | null;

  // Reimbursement Fees state
  reimbursementFees: ReimbursementFee[];
  isLoadingReimbursementFees: boolean;
  reimbursementFeesError: string | null;

  // Actions - Operator Config
  loadOperatorConfigs: () => Promise<void>;
  loadOperatorConfig: (operatorId: number) => Promise<void>;
  saveOperatorConfig: (request: UpsertOperatorBillingConfigRequest) => Promise<void>;
  deleteOperatorConfig: (operatorId: number) => Promise<void>;

  // Actions - Excluded Machines
  loadExcludedMachines: () => Promise<void>;
  addExcludedMachine: (request: AddExcludedMachineRequest) => Promise<void>;
  updateExcludedMachine: (machineId: number, reason: string | null) => Promise<void>;
  removeExcludedMachine: (machineId: number) => Promise<void>;

  // Actions - Reimbursement Fees
  loadReimbursementFees: (filters: Partial<ReimbursementFeesFilters>) => Promise<void>;
}

const getBillingApi = (): BillingApi => {
  const services = servicesContainer.getServices();
  return new BillingApi(BASE_AUTH_OFFICE_API, services.tokenManager);
};

export const useBillingStore = create<BillingStore>((set) => ({
  // Initial state
  operatorConfigs: [],
  isLoadingOperatorConfigs: false,
  operatorConfig: null,
  operatorConfigExists: false,
  isLoadingOperatorConfig: false,
  isSavingOperatorConfig: false,
  operatorConfigError: null,

  excludedMachines: [],
  isLoadingExcludedMachines: false,
  excludedMachinesError: null,

  reimbursementFees: [],
  isLoadingReimbursementFees: false,
  reimbursementFeesError: null,

  // ── Operator Config ────────────────────────────────────────────────────────

  loadOperatorConfigs: async () => {
    set({ isLoadingOperatorConfigs: true });
    try {
      const api = getBillingApi();
      const configs = await api.getOperatorConfigs();
      set({ operatorConfigs: configs, isLoadingOperatorConfigs: false });
    } catch {
      set({ isLoadingOperatorConfigs: false });
    }
  },

  deleteOperatorConfig: async (operatorId: number) => {
    const api = getBillingApi();
    await api.deleteOperatorConfig(operatorId);
    set((state) => ({
      operatorConfigs: state.operatorConfigs.filter((c) => c.operatorId !== operatorId),
    }));
  },

  loadOperatorConfig: async (operatorId: number) => {
    set({ isLoadingOperatorConfig: true, operatorConfigError: null });
    try {
      const api = getBillingApi();
      const config = await api.getOperatorConfig(operatorId);
      set({
        operatorConfig: config,
        operatorConfigExists: config !== null,
        isLoadingOperatorConfig: false,
      });
    } catch (error) {
      set({
        operatorConfigError: error instanceof Error ? error.message : 'Failed to load operator config',
        isLoadingOperatorConfig: false,
      });
    }
  },

  saveOperatorConfig: async (request: UpsertOperatorBillingConfigRequest) => {
    set({ isSavingOperatorConfig: true, operatorConfigError: null });
    try {
      const api = getBillingApi();
      // Re-read current exists flag at call time
      const storeState = useBillingStore.getState();
      if (storeState.operatorConfigExists) {
        await api.updateOperatorConfig(request);
      } else {
        await api.insertOperatorConfig(request);
        set({ operatorConfigExists: true });
      }
      set({
        operatorConfig: { ...request },
        isSavingOperatorConfig: false,
      });
    } catch (error) {
      set({
        operatorConfigError: error instanceof Error ? error.message : 'Failed to save operator config',
        isSavingOperatorConfig: false,
      });
      throw error;
    }
  },

  // ── Excluded Machines ──────────────────────────────────────────────────────

  loadExcludedMachines: async () => {
    set({ isLoadingExcludedMachines: true, excludedMachinesError: null });
    try {
      const api = getBillingApi();
      const machines = await api.getExcludedMachines();
      set({ excludedMachines: machines, isLoadingExcludedMachines: false });
    } catch (error) {
      set({
        excludedMachinesError: error instanceof Error ? error.message : 'Failed to load excluded machines',
        isLoadingExcludedMachines: false,
      });
    }
  },

  updateExcludedMachine: async (machineId: number, reason: string | null) => {
    const api = getBillingApi();
    await api.updateExcludedMachine(machineId, reason);
    set((state) => ({
      excludedMachines: state.excludedMachines.map((m) =>
        m.machineId === machineId ? { ...m, reason } : m
      ),
    }));
  },

  addExcludedMachine: async (request: AddExcludedMachineRequest) => {
    const api = getBillingApi();
    await api.addExcludedMachine(request);
    // Reload list after add
    const machines = await api.getExcludedMachines();
    set({ excludedMachines: machines });
  },

  removeExcludedMachine: async (machineId: number) => {
    const api = getBillingApi();
    await api.deleteExcludedMachine(machineId);
    // Remove optimistically from local state
    set((state) => ({
      excludedMachines: state.excludedMachines.filter((m) => m.machineId !== machineId),
    }));
  },

  // ── Reimbursement Fees ─────────────────────────────────────────────────────

  loadReimbursementFees: async (filters: Partial<ReimbursementFeesFilters>) => {
    set({ isLoadingReimbursementFees: true, reimbursementFeesError: null });
    try {
      const api = getBillingApi();
      const fees = await api.getReimbursementFees(filters);
      set({ reimbursementFees: fees, isLoadingReimbursementFees: false });
    } catch (error) {
      set({
        reimbursementFeesError: error instanceof Error ? error.message : 'Failed to load reimbursement fees',
        isLoadingReimbursementFees: false,
      });
    }
  },
}));
